import { ChevronRight, CircleCheck, X } from 'lucide-react';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect } from 'react';
import MapGL, { MapRef, Marker, NavigationControl } from 'react-map-gl';
import { Link } from 'react-router-dom';
import { Badge } from '../../components/badge';
import { useError } from '../../components/hooks/useError';
import { Label } from '../../components/label';
import { Spinner } from '../../components/spinner';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '../../components/ui/dialog';
import { getRangeLength } from '../../components/utils/time';
import { Api } from '../../core/api';
import { I18n } from '../../core/i18n';
import { MAPBOX_ACCESS_TOKEN } from '../../core/map';
import { links } from '../../core/router';
import { injectTSDI } from '../../core/tsdi';
import { Pin } from '../../facility/edit/facility-location/pins';
import { getStatusBadgeType, WorkoutStatusType } from '../list';
import { VerificationType } from '../types';
import { useVerificationDetails } from './store';

interface VerificationDialogProps {
    id?: number;
    type?: VerificationType;
    open: boolean;
    onClose(): void;
    /** Advance to the next item in the list (or close when none is left). */
    onNext(): void;
    /** Go back to the previous item in the list. */
    onPrevious(): void;
    /** Mark that an action happened so the list refreshes once closed. */
    onDirty(): void;
}

const Field = ({
    label,
    children
}: {
    label: string;
    children: React.ReactNode;
}) => (
    <div className="flex flex-col gap-1">
        <Label>{label}</Label>
        <div className="text-sm break-words">{children}</div>
    </div>
);

export const VerificationDialog = observer(
    ({
        id,
        type,
        open,
        onClose,
        onNext,
        onPrevious,
        onDirty
    }: VerificationDialogProps) => {
        const { fitnessActivityClient } = injectTSDI(Api);
        const { formatDateTime } = injectTSDI(I18n);
        const showError = useError();

        const request = useVerificationDetails(id, type);
        const status = request.fetched
            ? getStatusBadgeType(request.$.baseData)
            : undefined;

        const verify = useCallback(() => {
            if (id == null) {
                return Promise.resolve();
            }
            return type === VerificationType.WORKOUT
                ? fitnessActivityClient.admin.workoutSupportControllerVerify(id)
                : fitnessActivityClient.admin.facilityVisitSupportControllerVerify(
                      id
                  );
        }, [id, type]);

        const approve = useCallback(async () => {
            if (id == null) {
                return;
            }
            try {
                if (status?.statusType === WorkoutStatusType.NOT_VERIFIED) {
                    await verify();
                    onDirty();
                }
                onNext();
            } catch (error) {
                showError(error);
            }
        }, [id, status, verify, onDirty, onNext]);

        const reject = useCallback(async () => {
            if (id == null) {
                return;
            }
            try {
                if (status?.statusType !== WorkoutStatusType.REJECTED) {
                    await fitnessActivityClient.admin.activitySupportControllerRejectActivity(
                        id
                    );
                    await verify();
                    onDirty();
                }
                onNext();
            } catch (error) {
                showError(error);
            }
        }, [id, status, verify, onDirty, onNext]);

        const removeRejection = useCallback(async () => {
            if (id == null) {
                return;
            }
            try {
                await fitnessActivityClient.admin.activitySupportControllerDeleteActivityRejection(
                    id
                );
                onDirty();
                onNext();
            } catch (error) {
                showError(error);
            }
        }, [id, onDirty, onNext]);

        useEffect(() => {
            if (!open) {
                return;
            }
            const handler = (event: KeyboardEvent) => {
                if (event.key === 'ArrowRight') {
                    event.preventDefault();
                    onNext();
                } else if (event.key === 'ArrowLeft') {
                    event.preventDefault();
                    onPrevious();
                } else if (event.key === '+' || event.key === '=') {
                    event.preventDefault();
                    void approve();
                } else if (event.key === '-' || event.key === '_') {
                    event.preventDefault();
                    void reject();
                }
            };
            window.addEventListener('keydown', handler);
            return () => window.removeEventListener('keydown', handler);
        }, [open, onNext, onPrevious, approve, reject]);

        return (
            <Dialog
                open={open}
                onOpenChange={(value) => {
                    if (!value) {
                        onClose();
                    }
                }}
            >
                <DialogContent className="max-h-[90vh] gap-6 overflow-y-auto sm:max-w-4xl">
                    <DialogHeader>
                        <div className="flex flex-wrap items-center gap-2">
                            <DialogTitle>
                                Private workout Verification
                            </DialogTitle>
                            {status && (
                                <Badge type={status.badgeType}>
                                    {status.label}
                                </Badge>
                            )}
                        </div>
                        <DialogDescription>
                            Keyboard navigation: ← previous · → next · + approve
                            · − reject
                        </DialogDescription>
                    </DialogHeader>

                    {request.match({
                        loading: () => (
                            <div className="flex min-h-[300px] items-center justify-center">
                                <Spinner />
                            </div>
                        ),
                        fetched: ({ baseData, checkIn, photoUrl, user }) => (
                            <div className="flex flex-col gap-6">
                                <div className="grid gap-4 sm:grid-cols-3">
                                    <Field label="Workout ID">
                                        {baseData.id}
                                    </Field>
                                    <Field label="Workout Length">
                                        {getRangeLength(
                                            baseData.zonedDateTimeRangeModel
                                        )}
                                    </Field>
                                    <Field label="Date">
                                        {formatDateTime(
                                            baseData.zonedDateTimeRangeModel
                                                .from
                                        )}
                                    </Field>
                                    <Field label="Rejection Reason">
                                        {baseData.rejectionReason}
                                    </Field>
                                    <Field label="User Id">
                                        <Link
                                            className="text-primary hover:underline"
                                            to={links.userDetails(
                                                baseData.userId
                                            )}
                                        >
                                            {baseData.userId}
                                        </Link>
                                    </Field>
                                    <Field label="User">
                                        {user.firstName} {user.lastName}
                                    </Field>
                                    <Field label="Email">
                                        {user.emails
                                            .map((email) => email.value)
                                            .join(', ')}
                                    </Field>
                                </div>

                                {(checkIn?.startLocation || photoUrl) && (
                                    <div className="grid gap-4 md:grid-cols-2">
                                        {checkIn?.startLocation && (
                                            <Card>
                                                <CardContent className="flex flex-col gap-2">
                                                    <Label>Location</Label>
                                                    <WorkoutMap
                                                        latitude={
                                                            checkIn
                                                                .startLocation
                                                                .latitude
                                                        }
                                                        longitude={
                                                            checkIn
                                                                .startLocation
                                                                .longitude
                                                        }
                                                    />
                                                </CardContent>
                                            </Card>
                                        )}
                                        {photoUrl && (
                                            <Card>
                                                <CardContent className="flex flex-col gap-2">
                                                    <Label>Photo</Label>
                                                    <img
                                                        className="max-h-[400px] w-full rounded-md object-contain"
                                                        alt="Private workout image"
                                                        src={photoUrl}
                                                    />
                                                </CardContent>
                                            </Card>
                                        )}
                                    </div>
                                )}
                            </div>
                        )
                    })}

                    <DialogFooter className="sm:justify-between">
                        <Button variant="ghost" onClick={onClose}>
                            Close
                        </Button>
                        <div className="flex flex-wrap items-center gap-2">
                            {status?.statusType !==
                                WorkoutStatusType.REJECTED && (
                                <Button variant="destructive" onClick={reject}>
                                    <X />
                                    Reject
                                </Button>
                            )}
                            {status?.statusType ===
                                WorkoutStatusType.REJECTED && (
                                <Button
                                    variant="outline"
                                    onClick={removeRejection}
                                >
                                    <CircleCheck className="text-success" />
                                    Remove Rejection
                                </Button>
                            )}
                            {status?.statusType ===
                                WorkoutStatusType.NOT_VERIFIED && (
                                <Button onClick={approve}>
                                    <CircleCheck />
                                    Approve
                                </Button>
                            )}
                            <Button variant="ghost" onClick={onNext}>
                                Next
                                <ChevronRight />
                            </Button>
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        );
    }
);

const WorkoutMap = ({
    latitude,
    longitude
}: {
    latitude: number;
    longitude: number;
}) => {
    const mapGlRef = React.useRef<MapRef>(null);
    return (
        <div className="h-[400px] w-full overflow-hidden rounded-md">
            <MapGL
                initialViewState={{
                    longitude: longitude,
                    latitude: latitude,
                    zoom: 16,
                    bearing: 0,
                    pitch: 0
                }}
                mapStyle="mapbox://styles/mapbox/streets-v11"
                mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
                keyboard={false}
                ref={mapGlRef}
            >
                <Marker
                    key="marker"
                    longitude={longitude}
                    latitude={latitude}
                    anchor="bottom"
                    style={{ zIndex: 2 }}
                >
                    <Pin />
                </Marker>

                <NavigationControl position="top-left" />
            </MapGL>
        </div>
    );
};

export default VerificationDialog;
